import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { List, Badge, Button, Typography, Empty, Popover, Tooltip } from 'antd';
import { BellOutlined, CheckOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import useNotificationStore from '../../store/notificationStore';
import './NotificationDropdown.css';

dayjs.extend(relativeTime);

const { Text } = Typography;

const NotificationDropdown = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, fetchNotifications, markAsRead, markAllAsRead, initSocket, cleanupListeners } = useNotificationStore();

  useEffect(() => {
    fetchNotifications({ limit: 10 });

    const token = localStorage.getItem('accessToken');
    if (token) {
      initSocket(token);
    }

    // Poll for new notifications every 30s as a fallback
    const interval = setInterval(() => {
      fetchNotifications({ limit: 10 });
    }, 30000);

    return () => {
      clearInterval(interval);
      cleanupListeners();
    };
  }, []);

  const handleClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    const { entity_type, entity_id, project_id, issue_key, page_space_id, sprint_project_id, release_project_id } = notification;

    switch (entity_type) {
      case 'issue':
        if (project_id && issue_key) navigate(`/projects/${project_id}/issues/${issue_key}`);
        break;
      case 'page':
        if (page_space_id && entity_id) navigate(`/spaces/${page_space_id}/pages/${entity_id}`);
        break;
      case 'sprint':
        if (sprint_project_id) navigate(`/projects/${sprint_project_id}/board`);
        break;
      case 'release':
        if (release_project_id && entity_id) navigate(`/projects/${release_project_id}/releases/${entity_id}`);
        break;
      case 'project':
        if (entity_id) navigate(`/projects/${entity_id}`);
        break;
      default:
        break;
    }
  };

  const content = (
    <div className="notification-dropdown">
      <div className="notification-dropdown-header">
        <Text strong>Notifications</Text>
        {unreadCount > 0 && (
          <Button type="link" size="small" icon={<CheckOutlined />} onClick={markAllAsRead}>
            Mark all read
          </Button>
        )}
      </div>
      {notifications.length > 0 ? (
        <List
          size="small"
          dataSource={notifications.slice(0, 10)}
          renderItem={(item) => (
            <List.Item
              className={`notification-item ${!item.is_read ? 'notification-unread' : ''}`}
              onClick={() => handleClick(item)}
            >
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong style={{ fontSize: 13 }}>{item.title}</Text>
                  {!item.is_read && <Badge status="processing" />}
                </div>
                <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block' }}>{item.message}</Text>
                <Text style={{ fontSize: 11, color: '#bfbfbf' }}>{dayjs(item.created_at).fromNow()}</Text>
              </div>
            </List.Item>
          )}
        />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notifications" />
      )}
    </div>
  );

  return (
    <Popover content={content} trigger="click" placement="bottomRight" overlayStyle={{ width: 360 }}>
      <Tooltip title="Notifications">
        <Badge count={unreadCount} size="small" offset={[-4, 4]}>
          <Button type="text" icon={<BellOutlined style={{ fontSize: 18 }} />} />
        </Badge>
      </Tooltip>
    </Popover>
  );
};

export default NotificationDropdown;
